import dayjs from "dayjs";
import { cloneDeep, sum } from "lodash";
import {
    Box,
    Button,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ProductOrderFilterType, valueToText } from "../../../configs/filterTypes";
import { WarningDeclineProductOrder } from "../../../configs/productOrder";
import {
    ROLE,
    SALE_ORDER_STATUS,
    SALE_ORDER_TYPE,
} from "../../../data/enums";
import { AuthenticationContext } from "../../../provider/AuthenticationProvider";
import { ConfirmModal } from "../../../shared/components/ConfirmModal";
import DeclineModal from "../../../shared/components/GenericTextboxModal";
import LoadingButton from "../../../shared/components/LoadingButton";
import PageLoader from "../../../shared/components/LoadingPage";
import Status from "../../../shared/components/Status";
import { api } from "../../../shared/hooks/api";
import { StyledButton, TitleText, TitleWrapper } from "../../../shared/styled/TitleBar";
import Calendar from "../../../shared/svg/Calendar";
import POTemplate from "../../../templates/don_dat_hang.docx";
import apiMethods from "../../../utils/api";
import { generateDocument } from "../../../utils/exportUtils";
import {
    convertNumberToVietnameseMoneyText,
    formatNumber,
    formatNumberToCustomStyleWithoutDecimal,
    formatPercentForDocument,
} from "../../../utils/format";
import { error, success } from "../../../utils/toast";
import CreateFromProductStepper from "../../MaterialRequests/Content/CreateFromProductStepper";
import { getStatusColorTheme } from "../../ProductOrders/Content/statusTheme";
import { getSaleOrderEditCapabilities } from "./editCapabilities";
import { normalizeDeliveryTrip } from "./DeliveryTrip/normalizeDeliveryTrip";
import { ButtonRow } from "../create/Styled";
import SingleProductOrder from "../single";
import StatusProgress from "../single/StatusProgress";
import { ProductOrderWrapper } from "../Styled";
import {
    HeaderLeft,
    IconWrap,
    MetaBar,
    MetaItem,
    MetaLabel,
    MetaRow,
    MetaValue,
    ManufacturingOrderLine,
    ManufacturingOrderLineLabel,
    ManufacturingOrderLineValue,
    StyledModalButton,
    TitleRow,
} from "./Styled";
import SaleOrderTabNavigation from "./TabBar";
import {
    formatManufacturingOrderDisplayName,
    getRecommendedManufacturingCandidateIds,
} from "./manufacturingOrderSelection";
import { stripManufacturingOrderPrefix } from "../../../utils/manufacturingOrderCode";
import ReserveOrderForm from "../../ReserveOrder/ReserveOrderForm";
import ReserveImportProgress from "../../ReserveOrder/ReserveImportProgress";
import { CancellationBanner, OrderChangesTable, OrderHistory } from "./OrderHistory";
import { previewOrderChanges } from "./orderEditHistory";
import {
    getSaleOrderStatusLabel,
    getWorkshopForRole,
    hasReserveStorageAssignment,
    isReserveStorageAssignee,
} from "../../ReserveOrder/utils";
import { OrderChangeRow } from "../../../data/entities";

const processSaleOrder = (saleOrder: any) => {
    let products = [];
    let materials = [];

    if (saleOrder) {
        // Process products from items
        const items: any[] = saleOrder.items || [];

        products = items
            .filter((it: any) => !!it.product)
            .map((it: any) => ({
                ...it.product,
                product_price: Number(it.price) || 0,
                selectedQuantity: it.quantity,
                missingQuantity: 0, // Sale orders don't track missing quantity
                discount: it.discount ?? 0,
                markup: it.markup ?? 0,
                deliverySentQuantity: it.delivery_sent_quantity,
                deliveryExportedQuantity: it.delivery_exported_quantity,
                deliveryReturnedQuantity: it.delivery_returned_quantity,
                deliveryRemainingQuantity: it.delivery_remaining_quantity,
                item_type: "product",
                line_item_id: it.id, // Keep track of the item ID for updates
            }));

        materials = items
            .filter((it: any) => !!it.material)
            .map((it: any) => ({
                ...it.material,
                material_price: Number(it.price) || 0,
                selectedQuantity: it.quantity,
                missingQuantity: 0, // Sale orders don't track missing quantity
                discount: it.discount ?? 0,
                markup: it.markup ?? 0,
                deliverySentQuantity: it.delivery_sent_quantity,
                deliveryReturnedQuantity: it.delivery_returned_quantity,
                deliveryRemainingQuantity: it.delivery_remaining_quantity,
                item_type: "material",
                line_item_id: it.id, // Keep track of the item ID for updates
            }));

        saleOrder.created_at = saleOrder.edited_at
            ? dayjs(saleOrder.edited_at).format("YYYY-MM-DD")
            : dayjs().format("YYYY-MM-DD");
    }

    const transformedDeliveryTrips = (saleOrder?.delivery_trips || []).map(normalizeDeliveryTrip);

    return {
        ...saleOrder,
        edit_history: Array.isArray(saleOrder?.edit_history) ? saleOrder.edit_history : [],
        status: saleOrder ? (saleOrder.status || "").toString().toUpperCase() : "",
        notes: saleOrder ? (saleOrder.notes || "") : "",
        note: saleOrder ? (saleOrder.notes || "") : "",
        products: Array.isArray(saleOrder?.products) && saleOrder.products.length > 0 ? saleOrder.products : products,
        materials: Array.isArray(saleOrder?.materials) && saleOrder.materials.length > 0 ? saleOrder.materials : materials,
        delivery_trips: transformedDeliveryTrips,
    };
};

export const prepareSaleOrder = (saleOrder: any, originalData: any) => {
    // Build header
    const header: Record<string, any> = {
        due_date: saleOrder.due_date,
        notes: saleOrder.notes || "",
    };
    const poNumber = String(saleOrder.po_number || "").trim();

    if (saleOrder.status === SALE_ORDER_STATUS.OPEN) {
        header.po_number = poNumber;
    }

    // Get original items for comparison
    const originalItems: any[] = (originalData?.items || []) as any[];
    const originalById: Record<string, any> = {};
    originalItems.forEach((it: any) => {
        originalById[it.id] = it;
    });

    const currentItems = [
        ...(saleOrder.products || []),
        ...(saleOrder.materials || []),
    ];

    // Find deleted items
    const delete_item_ids: string[] = originalItems
        .filter((orig) => !currentItems.some((ci: any) => ci.line_item_id === orig.id))
        .map((orig) => orig.id);

    // Find updated items
    const update_items: any[] = currentItems
        .filter((ci: any) => !!ci.line_item_id && originalById[ci.line_item_id])
        .map((ci: any) => {
            const orig = originalById[ci.line_item_id];
            const newQuantity = Number(ci.selectedQuantity) || 0;
            const newPrice = Number(
                ci.item_type === "material" ? ci.material_price : ci.product_price,
            );
            const newDiscount = Number(ci.discount || 0);
            const newMarkup = Number(ci.markup || 0);

            const hasChanged =
                Number(orig.quantity || 0) !== newQuantity ||
                Number(orig.price || 0) !== newPrice ||
                Number(orig.discount || 0) !== newDiscount ||
                Number(orig.markup || 0) !== newMarkup;

            if (!hasChanged) return null;

            const updated: any = { id: ci.line_item_id };
            if (Number(orig.quantity || 0) !== newQuantity) {
                updated.quantity = String(newQuantity);
            }
            if (Number(orig.price || 0) !== newPrice) {
                updated.price = String(newPrice);
            }
            if (Number(orig.discount || 0) !== newDiscount) {
                updated.discount = String(newDiscount);
            }
            if (Number(orig.markup || 0) !== newMarkup) {
                updated.markup = String(newMarkup);
            }
            return updated;
        })
        .filter(Boolean) as any[];

    // Find added items
    const add_items: any[] = currentItems
        .filter((ci: any) => !ci.line_item_id)
        .map((ci: any) => {
            const price = Number(
                ci.item_type === "material" ? ci.material_price : ci.product_price,
            );
            const payload: any = {
                quantity: String(Number(ci.selectedQuantity) || 0),
                price: String(price),
                discount: String(Number(ci.discount || 0)),
                markup: String(Number(ci.markup || 0)),
            };
            if (ci.item_type === "material") {
                payload.material_id = ci.id;
                payload.product_id = null;
            } else {
                payload.product_id = ci.id;
                payload.material_id = null;
            }
            return payload;
        });

    return {
        header,
        add_items,
        update_items,
        delete_item_ids,
        // Note: Delivery trips are handled separately via sync API
        // delivery_trips: saleOrder.delivery_trips || []
    };
};

interface ManufacturingOrderCandidate {
    id: string;
    order_name: string;
    status: string;
    missing_qty: number;
    is_current_order: boolean;
    is_already_in_manufacturing_order: boolean;
    has_required_factory_assignment: boolean;
    assignment_message?: string;
}

interface ManufacturingOrderCandidateGroup {
    product: {
        id: string;
        name: string;
        product_code?: string;
        category?: string;
    };
    customer: {
        id: string;
        name: string;
    } | null;
    due_date: string;
    existing_manufacturing_order?: any;
    manufacturing_order_quantity_preview?: number | null;
    recommended_sale_order_ids?: string[];
    candidates: ManufacturingOrderCandidate[];
}

const SaleOrderDetail = () => {
    const { user: { role, id: userId } } = useContext(AuthenticationContext);
    const { productOrderId } = useParams();
    const [isView, setIsView] = useState(true);
    const [editOriginal, setEditOriginal] = useState<any>(null);
    const [pendingEdit, setPendingEdit] = useState<{ body: any; rows: OrderChangeRow[] } | null>(null);
    const savingRef = useRef(false);
    const [refreshCancellationFailed, setRefreshCancellationFailed] = useState(false);
    const [refreshingCancellation, setRefreshingCancellation] = useState(false);
    useEffect(() => {
        setEditOriginal(null);
        setPendingEdit(null);
        setIsView(true);
        setRefreshCancellationFailed(false);
    }, [productOrderId]);
    const [isCreateFromProductOpen, setIsCreateFromProductOpen] = useState(false);
    const [isCompleteConfirmOpen, setIsCompleteConfirmOpen] = useState(false);
    const [isOpenDeclineModal, setIsOpenDeclineModal] = useState(false);
    const [isDownloadingForm, setIsDownloadingForm] = useState(false);
    const [editingManufacturingOrder, setEditingManufacturingOrder] = useState<any>(null);
    const [manufacturingOrderCodeInput, setManufacturingOrderCodeInput] = useState("");
    const [isManufacturingOrderDetailOpen, setIsManufacturingOrderDetailOpen] = useState(false);
    const [isManufacturingCandidateOpen, setIsManufacturingCandidateOpen] = useState(false);
    const [isLoadingManufacturingCandidates, setIsLoadingManufacturingCandidates] = useState(false);
    const [manufacturingCandidateGroups, setManufacturingCandidateGroups] = useState<ManufacturingOrderCandidateGroup[]>([]);
    const [selectedManufacturingOrdersByProduct, setSelectedManufacturingOrdersByProduct] = useState<Record<string, string[]>>({});
    const [{ data, isLoading, setLocalUrl, setLocalData }, fetchSaleOrder] = api.get(
        `/product-flow/v2/orders/${productOrderId}/`,
    );
    const productOrder = useMemo(() => {
        return processSaleOrder(data);
    }, [data]);
    const isReserve = productOrder?.order_type === SALE_ORDER_TYPE.RESERVE;
    const isMatchingWorkshopManager =
        [
            ROLE.FACTORY_MANAGER_ONE,
            ROLE.FACTORY_MANAGER_TWO,
            ROLE.DEPUTY_FACTORY_MANAGER_ONE,
            ROLE.DEPUTY_FACTORY_MANAGER_TWO,
        ].includes(role) &&
        getWorkshopForRole(role) === productOrder?.workshop;
    const canManageReserveManufacturing =
        [ROLE.MANAGER, ROLE.PRODUCTION_STAFF].includes(role) ||
        isMatchingWorkshopManager;
    const canManageReserveImportRequest =
        role === ROLE.MANAGER || isMatchingWorkshopManager;
    const editCapabilities = useMemo(
        () =>
            getSaleOrderEditCapabilities({
                role,
                status: productOrder?.status,
                deliveryTrips: productOrder?.delivery_trips || [],
                orderType: productOrder?.order_type,
            }),
        [
            role,
            productOrder?.status,
            productOrder?.delivery_trips,
            productOrder?.order_type,
        ],
    );

    const [{ isUpdating, setLocalUrl: setPatchUrl }, patchSaleOrder] = api.patch(
        `/product-flow/v2/orders/${productOrderId}/`,
    );

    // Mutation for updating order status
    const [
        { isCreating: isUpdatingStatus, setLocalUrl: setStatusUrl },
        postUpdateStatus,
    ] = api.post(`/product-flow/v2/orders/${productOrderId}/status/`);
    const [
        { isCreating: isCreatingManufacturingOrder, setLocalUrl: setCreateManufacturingOrderUrl },
        postCreateManufacturingOrder,
    ] = api.post(`/product-flow/v2/orders/${productOrderId}/create-manufacturing-order/`);
    const [
        { isUpdating: isUpdatingManufacturingOrderCode, setLocalUrl: setManufacturingOrderGroupUrl },
        patchManufacturingOrderGroup,
    ] = api.patch("");

    useEffect(() => {
        setLocalUrl(`/product-flow/v2/orders/${productOrderId}/`);
        setPatchUrl(`/product-flow/v2/orders/${productOrderId}/`);
        setStatusUrl(`/product-flow/v2/orders/${productOrderId}/status/`);
        setCreateManufacturingOrderUrl(`/product-flow/v2/orders/${productOrderId}/create-manufacturing-order/`);
    }, [productOrderId, setLocalUrl, setPatchUrl, setStatusUrl, setCreateManufacturingOrderUrl]);

    useEffect(() => {
        if (editingManufacturingOrder?.id) {
            setManufacturingOrderGroupUrl(`/product-flow/v2/manufacturing-order-groups/${editingManufacturingOrder.id}/`);
        }
    }, [editingManufacturingOrder?.id, setManufacturingOrderGroupUrl]);

    // Fetch assignments and enable button when production_staff is assigned
    const [
        { data: assignmentsData },
        fetchAssignments,
    ] = api.get(`/product-flow/v2/assignments/`, {}, true);

    useEffect(() => {
        if (productOrderId) {
            fetchAssignments({ sale_order_id: productOrderId }, true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productOrderId]);

    const hasProductionAssigned = useMemo(() => {
        const dataObj: any = assignmentsData as any;
        if (!dataObj) return false;
        try {
            // Object keyed by role
            if (dataObj && typeof dataObj === "object" && !Array.isArray(dataObj)) {
                const arr = (dataObj["production_staff"] || dataObj["PRODUCTION_STAFF"]) as any[];
                return Array.isArray(arr) && arr.length > 0;
            }
            // Fallback list shape
            const list = Array.isArray(dataObj) ? dataObj : dataObj?.results || [];
            return list.some((it: any) => String(it?.role || "").toLowerCase() === "production_staff");
        } catch {
            return false;
        }
    }, [assignmentsData]);
    const hasAssignedReserveStorageKeeper = useMemo(
        () => hasReserveStorageAssignment(assignmentsData),
        [assignmentsData],
    );
    const canCurrentUserImportReserve = useMemo(
        () => isReserveStorageAssignee(assignmentsData, userId),
        [assignmentsData, userId],
    );

    const hasRequiredStorageKeepersAssigned = useMemo(() => {
        const dataObj: any = assignmentsData as any;
        if (productOrder?.status !== SALE_ORDER_STATUS.LOGISTIC_REVIEWED) return true;
        try {
            const products = (productOrder?.products || []) as any[];
            const hasVehicle = products.some((p: any) => {
                const cat = (p?.category || "").toString().toLowerCase();
                return (
                    cat.includes("cơ khí ô tô") ||
                    cat.includes("oto") ||
                    cat.includes("ô tô") ||
                    cat === "vehicle"
                );
            });

            const requirement: any = (data as any)?.requirement || (data as any)?.requirements || {};
            const missingQtyRaw: any = requirement?.missing_quantity;
            const isMissingQtyEmpty =
                missingQtyRaw === undefined || missingQtyRaw === null || missingQtyRaw === "" || Number(missingQtyRaw) === 0;
            const missingProductsArr: any[] = Array.isArray((data as any)?.requirements?.missing_products)
                ? (data as any).requirements.missing_products
                : [];
            const hasMissing = missingProductsArr.length > 0;
            const noManufacture = isMissingQtyEmpty && !hasMissing;

            const checkAssigned = (lowerRole: string) => {
                const upperRole = lowerRole.toUpperCase();
                if (dataObj && typeof dataObj === "object" && !Array.isArray(dataObj)) {
                    const arr = (dataObj[lowerRole] || dataObj[upperRole]) as any[];
                    return Array.isArray(arr) && arr.length > 0;
                }
                const list = Array.isArray(dataObj) ? dataObj : dataObj?.results || [];
                return list.some((it: any) => String(it?.role || "").toLowerCase() === lowerRole);
            };

            const inferRequiredStorageKeepers = (): string[] => {
                const keepers = new Set<string>();
                const items = Array.isArray((productOrder as any)?.items) ? (productOrder as any).items : [];

                if (items.length > 0) {
                    items.forEach((it: any) => {
                        const pStorage = it?.product?.storage || "";
                        const mStorage = it?.material?.storage || "";
                        if (/kho\s*1/i.test(String(pStorage)) || /kho\s*1/i.test(String(mStorage))) {
                            keepers.add("first_storage_keeper");
                        }
                        if (/kho\s*2/i.test(String(pStorage)) || /kho\s*2/i.test(String(mStorage))) {
                            keepers.add("second_storage_keeper");
                        }
                    });
                } else {
                    const prods: any[] = Array.isArray((productOrder as any)?.products) ? (productOrder as any).products : [];
                    const mats: any[] = Array.isArray((productOrder as any)?.materials) ? (productOrder as any).materials : [];
                    prods.forEach((p: any) => {
                        const s = p?.storage || "";
                        if (/kho\s*1/i.test(String(s))) keepers.add("first_storage_keeper");
                        if (/kho\s*2/i.test(String(s))) keepers.add("second_storage_keeper");
                    });
                    mats.forEach((m: any) => {
                        const s = m?.storage || "";
                        if (/kho\s*1/i.test(String(s))) keepers.add("first_storage_keeper");
                        if (/kho\s*2/i.test(String(s))) keepers.add("second_storage_keeper");
                    });
                }
                const anyLines = items.length > 0 || (productOrder as any)?.products?.length || (productOrder as any)?.materials?.length;
                if (keepers.size === 0 && anyLines) keepers.add("first_storage_keeper");
                return Array.from(keepers);
            };

            if (noManufacture) {
                const requiredKeepers = inferRequiredStorageKeepers();
                if (requiredKeepers.length === 0) return true;
                return requiredKeepers.every((r) => checkAssigned(r));
            }

            const lower = hasVehicle ? "factory_manager_two" : "factory_manager_one";
            return checkAssigned(lower);
        } catch {
            return false;
        }
    }, [assignmentsData, productOrder, data]);

    const hasRequiredAssignmentsForManufacturingExit = useMemo(() => {
        if (productOrder?.status !== SALE_ORDER_STATUS.MATERIAL_REQUESTED_AND_MANUFACTURING) return true;
        const dataObj: any = assignmentsData as any;
        try {
            const checkAssigned = (lowerRole: string) => {
                const upperRole = lowerRole.toUpperCase();
                if (dataObj && typeof dataObj === "object" && !Array.isArray(dataObj)) {
                    const arr = (dataObj[lowerRole] || dataObj[upperRole]) as any[];
                    return Array.isArray(arr) && arr.length > 0;
                }
                const list = Array.isArray(dataObj) ? dataObj : dataObj?.results || [];
                return list.some((it: any) => String(it?.role || "").toLowerCase() === lowerRole);
            };

            const products = (productOrder?.products || []) as any[];
            const hasVehicle = products.some((p: any) => {
                const cat = (p?.category || "").toString().toLowerCase();
                return (
                    cat.includes("cơ khí ô tô") ||
                    cat.includes("oto") ||
                    cat.includes("ô tô") ||
                    cat === "vehicle"
                );
            });
            const factoryRole = hasVehicle ? "factory_manager_two" : "factory_manager_one";
            const factoryAssigned = checkAssigned(factoryRole);

            const keepers = new Set<string>();
            const items = Array.isArray((productOrder as any)?.items) ? (productOrder as any).items : [];
            if (items.length > 0) {
                items.forEach((it: any) => {
                    const pStorage = it?.product?.storage || "";
                    const mStorage = it?.material?.storage || "";
                    if (/kho\s*1/i.test(String(pStorage)) || /kho\s*1/i.test(String(mStorage))) keepers.add("first_storage_keeper");
                    if (/kho\s*2/i.test(String(pStorage)) || /kho\s*2/i.test(String(mStorage))) keepers.add("second_storage_keeper");
                });
            } else {
                const prods: any[] = Array.isArray((productOrder as any)?.products) ? (productOrder as any).products : [];
                const mats: any[] = Array.isArray((productOrder as any)?.materials) ? (productOrder as any).materials : [];
                prods.forEach((p: any) => {
                    const s = p?.storage || "";
                    if (/kho\s*1/i.test(String(s))) keepers.add("first_storage_keeper");
                    if (/kho\s*2/i.test(String(s))) keepers.add("second_storage_keeper");
                });
                mats.forEach((m: any) => {
                    const s = m?.storage || "";
                    if (/kho\s*1/i.test(String(s))) keepers.add("first_storage_keeper");
                    if (/kho\s*2/i.test(String(s))) keepers.add("second_storage_keeper");
                });
            }
            const requiredKeepers = Array.from(keepers);
            const keepersAssigned = requiredKeepers.every((r) => checkAssigned(r));
            return factoryAssigned && keepersAssigned;
        } catch {
            return false;
        }
    }, [assignmentsData, productOrder]);

    const canSendToLogistic =
        productOrder.status === SALE_ORDER_STATUS.OPEN &&
        (isReserve || hasProductionAssigned);

    const missingProducts = useMemo(() => {
        try {
            const arr = ((data as any)?.requirements?.missing_products || []) as any[];
            return Array.isArray(arr) ? arr : [];
        } catch {
            return [] as any[];
        }
    }, [data]);

    const manufacturingOrders = useMemo(() => {
        const arr = ((data as any)?.manufacturing_orders || []) as any[];
        return Array.isArray(arr) ? arr : [];
    }, [data]);

    const manufacturingOrderCodesText = useMemo(() => {
        return manufacturingOrders
            .map(formatManufacturingOrderDisplayName)
            .join(", ");
    }, [manufacturingOrders]);

    const selectedManufacturingOrderTotal = useMemo(() => {
        return Object.values(selectedManufacturingOrdersByProduct).reduce(
            (total, ids) => total + ids.length,
            0,
        );
    }, [selectedManufacturingOrdersByProduct]);

    const requiredFactoryRoleGroups = useMemo(() => {
        const productById = new Map<string, any>();
        ((productOrder?.products || []) as any[]).forEach((product: any) => {
            productById.set(String(product?.id || ""), product);
        });

        const required = new Map<string, { roles: string[]; label: string }>();
        missingProducts.forEach((missingProduct: any) => {
            const product = productById.get(String(missingProduct?.product_id || ""));
            const category = String(product?.category || "").toLowerCase();
            const isVehicleWorkshop =
                category.includes("cơ khí ô tô") ||
                category.includes("oto") ||
                category.includes("ô tô") ||
                category === "vehicle";

            if (isVehicleWorkshop) {
                required.set("factory_manager_two", {
                    roles: ["factory_manager_two", "deputy_factory_manager_two"],
                    label: "quản lý xưởng cơ khí ô tô",
                });
            } else {
                required.set("factory_manager_one", {
                    roles: ["factory_manager_one", "deputy_factory_manager_one"],
                    label: "quản lý xưởng phuy/thùng",
                });
            }
        });

        return Array.from(required.values());
    }, [missingProducts, productOrder]);

    const hasRequiredFactoryManagersAssigned = useMemo(() => {
        if (productOrder?.status !== SALE_ORDER_STATUS.LOGISTIC_REVIEWED || missingProducts.length === 0) {
            return true;
        }

        const dataObj: any = assignmentsData as any;
        const checkAssigned = (lowerRole: string) => {
            const upperRole = lowerRole.toUpperCase();
            if (dataObj && typeof dataObj === "object" && !Array.isArray(dataObj)) {
                const arr = (dataObj[lowerRole] || dataObj[upperRole]) as any[];
                return Array.isArray(arr) && arr.length > 0;
            }
            const list = Array.isArray(dataObj) ? dataObj : dataObj?.results || [];
            return list.some((it: any) => String(it?.role || "").toLowerCase() === lowerRole);
        };

        return requiredFactoryRoleGroups.every((group) => (
            group.roles.some((roleName) => checkAssigned(roleName))
        ));
    }, [assignmentsData, missingProducts.length, productOrder?.status, requiredFactoryRoleGroups]);

    const missingFactoryAssignmentText = useMemo(() => {
        if (hasRequiredFactoryManagersAssigned) return "";
        const labels = requiredFactoryRoleGroups.map((group) => group.label).join(", ");
        return labels ? `Cần bàn giao cho ${labels} trước khi tạo lệnh sản xuất` : "Cần bàn giao quản lý xưởng trước khi tạo lệnh sản xuất";
    }, [hasRequiredFactoryManagersAssigned, requiredFactoryRoleGroups]);

    const onOpenManufacturingOrderCandidates = async () => {
        if (!productOrderId) return;
        setIsLoadingManufacturingCandidates(true);
        try {
            const response: any = await apiMethods.get(
                `/product-flow/v2/orders/${productOrderId}/manufacturing-order-candidates/`,
            );
            const groups: ManufacturingOrderCandidateGroup[] = Array.isArray(response?.groups)
                ? response.groups
                : [];
            const initialSelected: Record<string, string[]> = {};
            groups.forEach((group) => {
                const productId = String(group?.product?.id || "");
                if (!productId) return;
                initialSelected[productId] = getRecommendedManufacturingCandidateIds(group);
            });

            setManufacturingCandidateGroups(groups);
            setSelectedManufacturingOrdersByProduct(initialSelected);
            setIsManufacturingCandidateOpen(true);
        } catch (err) {
            error("Không tải được danh sách đơn hàng cùng lệnh sản xuất", err as any);
        } finally {
            setIsLoadingManufacturingCandidates(false);
        }
    };

    const toggleManufacturingCandidate = (productId: string, saleOrderId: string) => {
        setSelectedManufacturingOrdersByProduct((previous) => {
            const selected = new Set(previous[productId] || []);
            if (selected.has(saleOrderId)) {
                selected.delete(saleOrderId);
            } else {
                selected.add(saleOrderId);
            }
            return {
                ...previous,
                [productId]: Array.from(selected),
            };
        });
    };

    const selectEligibleManufacturingCandidates = (group: ManufacturingOrderCandidateGroup) => {
        const productId = String(group?.product?.id || "");
        if (!productId) return;
        const nextIds = (group.candidates || [])
            .filter((candidate) => candidate.has_required_factory_assignment || candidate.is_already_in_manufacturing_order)
            .map((candidate) => candidate.id);
        setSelectedManufacturingOrdersByProduct((previous) => ({
            ...previous,
            [productId]: Array.from(new Set(nextIds)),
        }));
    };

    const clearOptionalManufacturingCandidates = (group: ManufacturingOrderCandidateGroup) => {
        const productId = String(group?.product?.id || "");
        if (!productId) return;
        const requiredIds = (group.candidates || [])
            .filter((candidate) => candidate.is_current_order || candidate.is_already_in_manufacturing_order)
            .map((candidate) => candidate.id);
        setSelectedManufacturingOrdersByProduct((previous) => ({
            ...previous,
            [productId]: Array.from(new Set(requiredIds)),
        }));
    };

    const onCreateManufacturingOrder = async () => {
        const payloadGroups = manufacturingCandidateGroups
            .map((group) => {
                const productId = String(group?.product?.id || "");
                return {
                    product_id: productId,
                    sale_order_ids: selectedManufacturingOrdersByProduct[productId] || [],
                };
            })
            .filter((group) => group.product_id && group.sale_order_ids.length > 0);

        try {
            await postCreateManufacturingOrder({ groups: payloadGroups });
            await fetchSaleOrder();
            setIsManufacturingCandidateOpen(false);
            setManufacturingCandidateGroups([]);
            setSelectedManufacturingOrdersByProduct({});
            success("THÀNH CÔNG", "Đã tạo lệnh sản xuất");
        } catch (err) {
            error("Tạo lệnh sản xuất thất bại", err as any);
        }
    };

    const onCreateReserveManufacturingOrders = async () => {
        const groups = missingProducts
            .map((item: any) => ({
                product_id: item.product_id,
                sale_order_ids: [productOrderId],
            }))
            .filter((group: any) => group.product_id && productOrderId);
        try {
            await postCreateManufacturingOrder({ groups });
            await fetchSaleOrder();
            success("THÀNH CÔNG", "Đã tạo lệnh sản xuất cho đơn dự phòng");
        } catch (err) {
            error("Tạo lệnh sản xuất thất bại", err as any);
        }
    };

    const onEditManufacturingOrderCode = (manufacturingOrder: any) => {
        setEditingManufacturingOrder(manufacturingOrder);
        setManufacturingOrderCodeInput(
            stripManufacturingOrderPrefix(
                manufacturingOrder?.manufacturing_order_code,
            ),
        );
        if (manufacturingOrder?.id) {
            setManufacturingOrderGroupUrl(`/product-flow/v2/manufacturing-order-groups/${manufacturingOrder.id}/`);
        }
    };

    const onConfirmManufacturingOrderCode = async () => {
        if (!editingManufacturingOrder?.id) return;
        try {
            await patchManufacturingOrderGroup({
                manufacturing_order_code: manufacturingOrderCodeInput.trim(),
            });
            await fetchSaleOrder();
            success("THÀNH CÔNG", "Đã cập nhật lệnh sản xuất");
            setEditingManufacturingOrder(null);
            setManufacturingOrderCodeInput("");
        } catch (err) {
            error("Cập nhật lệnh sản xuất thất bại", err as any);
        }
    };

    const onClickDownloadingProductOrderForm = async () => {
        const items = [...(productOrder.products || []), ...(productOrder.materials || [])];
        const getItemPrice = (item: any) =>
            item.item_type === "material" ? item.material_price : item.product_price;
        // Pre-tax amount per line = price * (1 - discount%) * qty. This is "Thành tiền" on the invoice.
        const calculateLineBase = (item: any) => {
            const itemPrice = getItemPrice(item) || 0;
            const discount = item.discount || 0;
            const qty = item.selectedQuantity || 0;
            return Math.ceil(itemPrice * ((100 - discount) / 100) * qty);
        };
        // Per-line tax = base * markup%.
        const calculateLineTax = (item: any) => {
            const markup = item.markup || 0;
            return Math.ceil((calculateLineBase(item) * markup) / 100);
        };

        const subtotalPreTax = sum(items.map(calculateLineBase)) || 0;
        const taxAmount = sum(items.map(calculateLineTax)) || 0;
        const totalWithTax = subtotalPreTax + taxAmount;

        const formData = {
            customer_name: productOrder.customer?.name || "",
            address: productOrder.customer?.address || "",
            tax_ID: productOrder.customer?.tax_id || "",
            phone_number: productOrder.customer?.phone || "",
            email: productOrder.customer?.email || "",
            order_name: productOrder.name || "",
            po_number: productOrder.po_number || "",
            order_note: productOrder.note || "",
            order_date: productOrder.created_at || "",
            due_date: productOrder.due_date || "",
            items: items.map((item: any, index: number) => ({
                index: index + 1,
                name: item?.name || "",
                quantity: formatNumberToCustomStyleWithoutDecimal(item.selectedQuantity || 0) || "",
                code: item?.product_code || item?.material_code || "",
                price: formatNumber(getItemPrice(item) || 0),
                discount: formatPercentForDocument(Number(item?.discount) || 0),
                tax: formatPercentForDocument(Number(item?.markup) || 0),
                product_total_price: formatNumber(calculateLineBase(item || {})),
            })),
            order_total_price: formatNumberToCustomStyleWithoutDecimal(subtotalPreTax) || "",
            tax: formatNumberToCustomStyleWithoutDecimal(taxAmount) || "",
            price_tax_combined: formatNumberToCustomStyleWithoutDecimal(totalWithTax) || "",
            total_value_in_text: convertNumberToVietnameseMoneyText(totalWithTax) || "",
            delivery_destination: productOrder?.customer?.address || "",
        };

        generateDocument(formData, POTemplate, `don_dat_hang_${productOrder.name}`);
    };

    const mergeDisplayedProductOrder = (updatedData: any) => {
        const newData = {
            ...data,
            ...updatedData,
            products:
                updatedData.products !== undefined
                    ? updatedData.products
                    : data?.products,
            materials:
                updatedData.materials !== undefined
                    ? updatedData.materials
                    : data?.materials,
            delivery_trips:
                updatedData.delivery_trips !== undefined
                    ? updatedData.delivery_trips
                    : data?.delivery_trips,
        };
        setLocalData(newData);
    };

    const productOrderInfoTab = isLoading ? (
        <PageLoader />
    ) : (
        <ProductOrderWrapper>
            {isReserve ? (
                <>
                    <ReserveImportProgress
                        progress={productOrder.reserve_import_progress}
                    />
                    <ReserveOrderForm
                        productOrder={productOrder as any}
                        mergeProductOrder={mergeDisplayedProductOrder}
                        isView={isView}
                        onAssignmentsUpdated={() => {
                            if (productOrderId) {
                                fetchAssignments(
                                    { sale_order_id: productOrderId },
                                    true,
                                );
                            }
                        }}
                    />
                </>
            ) : (
                <SingleProductOrder
                    productOrder={productOrder as any}
                    mergeProductOrder={mergeDisplayedProductOrder}
                    isView={isView}
                    disableItemEditing={!editCapabilities.canEditFullOrder}
                    disableHeaderEditing={!editCapabilities.canEditFullOrder}
                    showPoField={false}
                    onAssignmentsUpdated={() => {
                        if (productOrderId) {
                            fetchAssignments(
                                { sale_order_id: productOrderId },
                                true,
                            );
                        }
                    }}
                />
            )}
            {!isReserve && <OrderHistory events={productOrder.edit_history} />}
        </ProductOrderWrapper>
    );

    const hasUndeliveredTrips = useMemo(() => {
        const trips = (productOrder?.delivery_trips || []) as any[];
        if (!Array.isArray(trips) || trips.length === 0) return false;
        return trips.some((t: any) => String(t?.status || "").toUpperCase() !== "DELIVERED");
    }, [productOrder]);

    const isDeliveryQuantityMismatch = useMemo(() => {
        try {
            const orderQtyProducts = (productOrder?.products || []).reduce(
                (acc: number, it: any) => acc + Number(it?.selectedQuantity || 0),
                0,
            );
            const orderQtyMaterials = (productOrder?.materials || []).reduce(
                (acc: number, it: any) => acc + Number(it?.selectedQuantity || 0),
                0,
            );
            const totalOrderQty = Number(orderQtyProducts) + Number(orderQtyMaterials);

            const trips = (productOrder?.delivery_trips || []) as any[];
            const totalTripQty = (trips || []).reduce((accTrip: number, trip: any) => {
                const items = (trip?.delivery_products || trip?.items || []) as any[];
                const s = (items || []).reduce(
                    (acc: number, di: any) => acc + Number(di?.quantity || 0),
                    0,
                );
                return accTrip + s;
            }, 0);

            return Number(totalTripQty) !== Number(totalOrderQty);
        } catch {
            return false;
        }
    }, [productOrder]);

    const completeWarningText = useMemo(() => {
        const messages: string[] = [];
        if (!isReserve && hasUndeliveredTrips) {
            messages.push(
                "Có chuyến vận chuyển chưa đc hoàn thành, nếu bạn tiếp tục, tất cả các chuyến vận chuyển sẽ đổi thành trạng thai hoàn thành",
            );
        }
        if (!isReserve && isDeliveryQuantityMismatch) {
            messages.push(
                "Tổng số lượng sản phẩm, trong các chuyến vận chuyển khác với số lượng yêu câu của đơn hàng",
            );
        }
        if (messages.length === 0) {
            return isReserve
                ? "Xác nhận hoàn thành đơn dự phòng?"
                : "Xác nhận hoàn thành đơn hàng?";
        }
        if (messages.length === 1) return messages[0];
        return messages.join(" hoặc ");
    }, [hasUndeliveredTrips, isDeliveryQuantityMismatch, isReserve]);

    const completeWarningMessages = useMemo(() => {
        const arr: string[] = [];
        if (!isReserve && hasUndeliveredTrips) {
            arr.push(
                "Có chuyến vận chuyển chưa đc hoàn thành, nếu bạn tiếp tục, tất cả các chuyến vận chuyển sẽ đổi thành trạng thai hoàn thành",
            );
        }
        if (!isReserve && isDeliveryQuantityMismatch) {
            arr.push(
                "Tổng số lượng sản phẩm, trong các chuyến vận chuyển khác với số lượng yêu câu của đơn hàng",
            );
        }
        return arr;
    }, [hasUndeliveredTrips, isDeliveryQuantityMismatch, isReserve]);

    const refreshCancellation = async () => {
        setRefreshingCancellation(true);
        try {
            const refreshed = await apiMethods.get(`/product-flow/v2/orders/${productOrderId}/`);
            setLocalData(refreshed);
            setRefreshCancellationFailed(false);
        } catch (err) {
            setRefreshCancellationFailed(true);
            error("Đơn đã huỷ, nhưng chưa tải được thông tin mới. Vui lòng tải lại.", err);
        } finally {
            setRefreshingCancellation(false);
        }
    };

    const saveOrder = async (body: any, reason?: string) => {
        if (savingRef.current) return;
        savingRef.current = true;
        try {
            const saved = await patchSaleOrder(reason ? { ...body, reason: reason.trim() } : body);
            setLocalData(saved);
            setPendingEdit(null);
            setEditOriginal(null);
            setIsView(true);
            success("THÀNH CÔNG", "Cập nhật đơn hàng thành công");
        } catch (err: any) {
            error("Cập nhật đơn hàng thất bại", err?.message || err);
        } finally {
            savingRef.current = false;
        }
    };

    const requestSave = async () => {
        if (savingRef.current || isUpdating) return;
        const original = editOriginal || data;
        const body = editCapabilities.canEditFullOrder ? prepareSaleOrder(productOrder, original) : {
            header: { notes: productOrder.notes || "" }, add_items: [], update_items: [], delete_item_ids: [],
        };
        const rows = isReserve ? [] : previewOrderChanges(body, original, productOrder);
        if (rows.length) setPendingEdit({ body, rows });
        else await saveOrder(body);
    };

    const onClickDecline = async (reason?: string) => {
        if (isUpdatingStatus) return;
        const trimmedReason = (reason || "").trim();
        if (!trimmedReason) return;
        try {
            await postUpdateStatus({
                status: SALE_ORDER_STATUS.CANCELLED,
                ...(isReserve ? { notes: trimmedReason } : { reason: trimmedReason }),
            });
        } catch (err) {
            error("Hủy đơn hàng thất bại", err);
            return;
        }
        setIsOpenDeclineModal(false);
        setLocalData({ ...data, status: SALE_ORDER_STATUS.CANCELLED, cancel_reason: trimmedReason,
            cancelled_at: null, cancelled_by: null });
        success("THÀNH CÔNG", "Đã hủy đơn hàng thành công");
        await refreshCancellation();
    };

    return (
        <>
            {isCreateFromProductOpen && (
                <CreateFromProductStepper
                    open={isCreateFromProductOpen}
                    onClose={() => setIsCreateFromProductOpen(false)}
                    isAllowToSearchProduct={false}
                    initialProducts={(productOrder?.products || []).map((p: any) => ({
                        ...p,
                        production_quantity: Number(p.selectedQuantity || 0),
                    }))}
                    lockedSaleOrder={
                        isReserve
                            ? {
                                  id: productOrderId,
                                  order_name:
                                      productOrder?.order_name ||
                                      productOrder?.name,
                              }
                            : undefined
                    }
                />
            )}

            {isOpenDeclineModal && (
                <DeclineModal
                    open={isOpenDeclineModal}
                    managed={!isReserve}
                    title={isReserve ? WarningDeclineProductOrder.title : "Huỷ đơn hàng này?"}
                    contentText={isReserve ? WarningDeclineProductOrder.context : [
                        `Đơn ${productOrder.order_name || productOrder.name} sẽ bị huỷ. Đơn vẫn nằm trong danh sách; từ lúc huỷ thì không nhập kho và không xuất kho được nữa.`,
                    ]}
                    reasonText={isReserve ? WarningDeclineProductOrder.reason : "Lý do huỷ"}
                    helperText={isReserve ? undefined : "Lý do huỷ được lưu riêng, không ghi đè Ghi chú đơn hàng."}
                    cancelButtonText={isReserve ? "Đóng" : "Quay lại"}
                    confirmColor={isReserve ? undefined : "#a83222"}
                    handleConfirm={onClickDecline}
                    handleClose={() => setIsOpenDeclineModal(false)}
                    buttonText="Hủy đơn hàng"
                    isLoading={isUpdatingStatus}
                />
            )}
            {pendingEdit && <DeclineModal
                open managed title="Lý do thay đổi" reasonText="Lý do"
                contentText={[`Đơn ${productOrder.order_name || productOrder.name} có ${pendingEdit.rows.length} thay đổi cần ghi lý do.`]}
                helperText="Lý do được ghi vào Lịch sử thay đổi của đơn."
                cancelButtonText="Quay lại" buttonText="Lưu thay đổi"
                isLoading={isUpdating}
                handleClose={() => { if (!savingRef.current) setPendingEdit(null); }}
                handleConfirm={async reason => { await saveOrder(pendingEdit.body, reason); }}
            ><OrderChangesTable rows={pendingEdit.rows} /></DeclineModal>}

            <ProductOrderWrapper>
                {!isReserve && productOrder.status === SALE_ORDER_STATUS.CANCELLED && <CancellationBanner
                    order={productOrder}
                    retry={refreshCancellationFailed ? () => { void refreshCancellation(); } : undefined}
                    refreshing={refreshingCancellation}
                />}
                <TitleWrapper>
                    <HeaderLeft>
                        <TitleRow>
                            <TitleText>
                                {productOrder?.name ||
                                    productOrder?.order_name ||
                                    (isReserve
                                        ? "Đơn dự phòng"
                                        : "Đơn bán hàng")}
                            </TitleText>
                            <Status
                                colorTheme={getStatusColorTheme(productOrder.status)}
                                text={getSaleOrderStatusLabel(
                                    productOrder.status,
                                    isReserve,
                                )}
                                width="124px"
                            />
                        </TitleRow>
                        <MetaRow>
                            <MetaBar>
                                {!isReserve && (
                                    <MetaItem>
                                        <MetaLabel>Mã PO</MetaLabel>
                                        {!isView &&
                                        editCapabilities.canEditFullOrder &&
                                        String(productOrder?.status || "").toUpperCase() ===
                                            SALE_ORDER_STATUS.OPEN ? (
                                            <TextField
                                                value={productOrder?.po_number || ""}
                                                onChange={(event) => {
                                                    mergeDisplayedProductOrder({
                                                        po_number: event.target.value,
                                                    });
                                                }}
                                                placeholder="Nhập mã PO"
                                                inputProps={{
                                                    maxLength: 100,
                                                    "aria-label": "Mã PO",
                                                }}
                                                variant="standard"
                                                size="small"
                                                sx={{
                                                    width: 180,
                                                    "& .MuiInputBase-input": {
                                                        fontFamily: "Noto Sans",
                                                        fontSize: "15px",
                                                        fontWeight: 600,
                                                        padding: "1px 0 2px",
                                                    },
                                                    "& .MuiFormHelperText-root": {
                                                        fontFamily: "Noto Sans",
                                                        margin: "3px 0 0",
                                                    },
                                                }}
                                            />
                                        ) : (
                                            <MetaValue>{productOrder?.po_number || "—"}</MetaValue>
                                        )}
                                    </MetaItem>
                                )}
                                {productOrder?.due_date && (
                                    <MetaItem>
                                        <IconWrap>
                                            <Calendar/>
                                        </IconWrap>
                                        <MetaLabel>Hạn</MetaLabel>
                                        <MetaValue>
                                            {dayjs(productOrder.due_date).format("DD/MM/YYYY")}
                                        </MetaValue>
                                    </MetaItem>
                                )}
                            </MetaBar>
                        </MetaRow>
                    </HeaderLeft>

                    <ButtonRow>
                        {editCapabilities.canShowCancelOrder && (
                            <Tooltip
                                title={
                                    editCapabilities.hasDepartedTrip
                                        ? "Không thể huỷ vì đã có chuyến bắt đầu xuất kho hoặc vận chuyển"
                                        : ""
                                }
                            >
                                <span>
                                    <StyledButton
                                        variant="text"
                                        onClick={() => setIsOpenDeclineModal(true)}
                                        disabled={
                                            !editCapabilities.canCancelOrder ||
                                            isUpdatingStatus
                                        }
                                    >
                                        Huỷ đơn hàng
                                    </StyledButton>
                                </span>
                            </Tooltip>
                        )}
                        {!isReserve && (
                            <LoadingButton
                                variant="text"
                                isLoading={isDownloadingForm}
                                disabled={isDownloadingForm}
                                text="Tải đơn bán hàng"
                                onClick={async () => {
                                    setIsDownloadingForm(true);
                                    try {
                                        await onClickDownloadingProductOrderForm();
                                    } finally {
                                        setIsDownloadingForm(false);
                                    }
                                }}
                            />
                        )}
                        {(isReserve
                            ? [
                                  ROLE.MANAGER,
                                  ROLE.LOGISTIC_MANAGER,
                                  ROLE.PRODUCTION_STAFF,
                              ].includes(role)
                            : [
                                  ROLE.MANAGER,
                                  ROLE.SALE_MANAGER,
                                  ROLE.LOGISTIC_MANAGER,
                                  ROLE.SALE_STAFF,
                              ].includes(role)) &&
                            productOrder.status === SALE_ORDER_STATUS.OPEN && (
                            <LoadingButton
                                variant="contained"
                                text="Chuyển cho hậu cần"
                                isLoading={isUpdatingStatus}
                                disabled={!canSendToLogistic || isUpdatingStatus}
                                onClick={async () => {
                                    try {
                                        const res: any = await postUpdateStatus({
                                            status: SALE_ORDER_STATUS.LOGISTIC_REVIEWED,
                                            notes: productOrder?.note || "",
                                        });
                                        await fetchSaleOrder();
                                        const prev = String(
                                            res?.previous_status || "",
                                        ).toUpperCase();
                                        const prevLabel =
                                            valueToText(ProductOrderFilterType, "status", prev) ||
                                            "Mở";
                                        success(
                                            "THÀNH CÔNG",
                                            `Đơn hàng ${prevLabel} chuyển cho Hậu Cần`,
                                        );
                                    } catch (err) {
                                        error("Chuyển cho hậu cần thất bại", err as any);
                                    }
                                }}
                            />
                        )}

                        {(isReserve
                            ? canManageReserveManufacturing
                            : [
                                  ROLE.MANAGER,
                                  ROLE.LOGISTIC_MANAGER,
                                  ROLE.PRODUCTION_STAFF,
                              ].includes(role)) &&
                            productOrder.status === SALE_ORDER_STATUS.LOGISTIC_REVIEWED &&
                            missingProducts.length > 0 && (
                                <Tooltip
                                    title={
                                        isReserve
                                            ? ""
                                            : missingFactoryAssignmentText
                                    }
                                    disableHoverListener={
                                        isReserve ||
                                        hasRequiredFactoryManagersAssigned
                                    }
                                >
                                    <span>
                                        <LoadingButton
                                            variant="contained"
                                            text="Tạo lệnh sản xuất"
                                            isLoading={isLoadingManufacturingCandidates || isCreatingManufacturingOrder}
                                            disabled={
                                                (!isReserve &&
                                                    !hasRequiredFactoryManagersAssigned) ||
                                                isLoadingManufacturingCandidates ||
                                                isCreatingManufacturingOrder
                                            }
                                            onClick={
                                                isReserve
                                                    ? onCreateReserveManufacturingOrders
                                                    : onOpenManufacturingOrderCandidates
                                            }
                                        />
                                    </span>
                                </Tooltip>
                            )}
                        {!isReserve &&
                            [ROLE.MANAGER, ROLE.PRODUCTION_STAFF, ROLE.LOGISTIC_MANAGER].includes(role) && productOrder.status === SALE_ORDER_STATUS.LOGISTIC_REVIEWED &&
                            missingProducts.length === 0 && (
                                <LoadingButton
                                    variant="contained"
                                    text="Chuyển qua xuất kho"
                                    isLoading={isUpdatingStatus}
                                    disabled={!hasRequiredStorageKeepersAssigned || isUpdatingStatus}
                                    onClick={async () => {
                                        try {
                                            const res: any = await postUpdateStatus({
                                                status: SALE_ORDER_STATUS.IMPORTING_AND_EXPORTING,
                                                notes: productOrder?.note || "",
                                            });
                                            await fetchSaleOrder();
                                            const prev = String(
                                                res?.previous_status || "",
                                            ).toUpperCase();
                                            const prevLabel =
                                                valueToText(
                                                    ProductOrderFilterType,
                                                    "status",
                                                    prev,
                                                ) || "Mở";
                                            success(
                                                "THÀNH CÔNG",
                                                `Đơn hàng ${prevLabel} chuyển qua xuất kho`,
                                            );
                                        } catch (err) {
                                            error("Chuyển qua xuất kho thất bại", err as any);
                                        }
                                    }}
                                />
                            )}

                        {(isReserve
                            ? canManageReserveImportRequest ||
                              role === ROLE.PRODUCTION_STAFF
                            : [
                                  ROLE.MANAGER,
                                  ROLE.LOGISTIC_MANAGER,
                                  ROLE.FACTORY_MANAGER_ONE,
                                  ROLE.FACTORY_MANAGER_TWO,
                                  ROLE.DEPUTY_FACTORY_MANAGER_ONE,
                                  ROLE.DEPUTY_FACTORY_MANAGER_TWO,
                              ].includes(role)) &&
                            productOrder.status === SALE_ORDER_STATUS.MATERIAL_REQUESTED_AND_MANUFACTURING && (
                                <>
                                    <StyledButton
                                        variant="outlined"
                                        onClick={() => setIsCreateFromProductOpen(true)}
                                    >
                                        Tạo phiếu lãnh vật tư
                                    </StyledButton>
                                    <Tooltip
                                        title={
                                            isReserve &&
                                            !hasAssignedReserveStorageKeeper
                                                ? "Cần bàn giao cho thủ kho trước khi chuyển tới nhập kho"
                                                : ""
                                        }
                                    >
                                        <span>
                                            <LoadingButton
                                                variant="contained"
                                                text={
                                                    isReserve
                                                        ? "Chuyển tới nhập kho"
                                                        : "Chuyển tới nhập kho và xuất kho"
                                                }
                                                isLoading={isUpdatingStatus}
                                                disabled={
                                                    isUpdatingStatus ||
                                                    (isReserve
                                                        ? !hasAssignedReserveStorageKeeper
                                                        : !hasRequiredAssignmentsForManufacturingExit)
                                                }
                                                onClick={async () => {
                                                    try {
                                                        const res: any = await postUpdateStatus({
                                                            status: SALE_ORDER_STATUS.IMPORTING_AND_EXPORTING,
                                                            notes: productOrder?.note || "",
                                                        });
                                                        await fetchSaleOrder();
                                                        const prev = String(
                                                            res?.previous_status || "",
                                                        ).toUpperCase();
                                                        const prevLabel =
                                                            valueToText(
                                                                ProductOrderFilterType,
                                                                "status",
                                                                prev,
                                                            ) || "Mở";
                                                        success(
                                                            "THÀNH CÔNG",
                                                            `Đơn hàng ${prevLabel} chuyển qua đang nhập kho`,
                                                        );
                                                    } catch (err) {
                                                        error("Chuyển qua đang nhập kho thất bại", err as any);
                                                    }
                                                }}
                                            />
                                        </span>
                                    </Tooltip>
                                </>
                            )}

                        {(isReserve
                            ? [
                                  ROLE.LOGISTIC_MANAGER,
                                  ROLE.PRODUCTION_STAFF,
                                  ROLE.MANAGER,
                              ].includes(role)
                            : [ROLE.FIRST_STORAGE_KEEPER, ROLE.SECOND_STORAGE_KEEPER, ROLE.PURCHASING_STAFF].includes(role) ||
                              assignmentsData?.FIRST_STORAGE_KEEPER?.at(0)?.user_id === userId ||
                              [ROLE.LOGISTIC_MANAGER, ROLE.PRODUCTION_STAFF, ROLE.MANAGER].includes(role)) &&
                            productOrder.status === SALE_ORDER_STATUS.IMPORTING_AND_EXPORTING && (
                            <>
                                <Tooltip
                                    title={
                                        isReserve &&
                                        !productOrder
                                            ?.reserve_import_progress
                                            ?.is_fully_imported
                                            ? "Cần nhập đủ toàn bộ sản phẩm trước khi hoàn thành"
                                            : ""
                                    }
                                >
                                    <span>
                                        <LoadingButton
                                            variant="contained"
                                            text={
                                                isReserve
                                                    ? "Hoàn thành đơn dự phòng"
                                                    : "Hoàn thành đơn hàng"
                                            }
                                            isLoading={isUpdatingStatus}
                                            disabled={
                                                isUpdatingStatus ||
                                                (isReserve &&
                                                    !productOrder
                                                        ?.reserve_import_progress
                                                        ?.is_fully_imported)
                                            }
                                            onClick={() =>
                                                setIsCompleteConfirmOpen(true)
                                            }
                                        />
                                    </span>
                                </Tooltip>
                            </>
                        )}
                        {isView && editCapabilities.canEditNotes ? (
                            <StyledButton
                                variant="contained"
                                onClick={() => {
                                    setEditOriginal(cloneDeep(data));
                                    setIsView(false);
                                }}
                            >
                                Chỉnh sửa
                            </StyledButton>
                        ) : (
                            !isView && (
                                <>
                                    <StyledButton
                                        disabled={isUpdating}
                                        onClick={async () => {
                                            if (editOriginal) setLocalData(cloneDeep(editOriginal));
                                            setEditOriginal(null);
                                            setPendingEdit(null);
                                            setIsView(true);
                                        }}
                                    >
                                        Huỷ
                                    </StyledButton>
                                    <LoadingButton
                                        variant="contained"
                                        text="Cập nhật"
                                        isLoading={isUpdating}
                                        disabled={isUpdating}
                                        onClick={requestSave}
                                    />
                                </>
                            )
                        )}
                    </ButtonRow>
                </TitleWrapper>

                {manufacturingOrders.length > 0 && (
                    <Tooltip title={manufacturingOrderCodesText}>
                        <ManufacturingOrderLine
                            type="button"
                            onClick={() => setIsManufacturingOrderDetailOpen(true)}
                        >
                            <ManufacturingOrderLineLabel>Lệnh sản xuất</ManufacturingOrderLineLabel>
                            <ManufacturingOrderLineValue>{manufacturingOrderCodesText}</ManufacturingOrderLineValue>
                        </ManufacturingOrderLine>
                    </Tooltip>
                )}

                <StatusProgress
                    status={productOrder?.status as any}
                    isReserve={isReserve}
                />

                <SaleOrderTabNavigation
                    productOrderInfoTab={productOrderInfoTab}
                    productOrder={productOrder as any}
                    isReserve={isReserve}
                    canImportReserve={canCurrentUserImportReserve}
                    mergeProductOrder={(updatedData: any) => {
                        const newData = {
                            ...data,
                            ...updatedData,
                            products: updatedData.products !== undefined ? updatedData.products : data?.products,
                            materials: updatedData.materials !== undefined ? updatedData.materials : data?.materials,
                            delivery_trips: updatedData.delivery_trips !== undefined ? updatedData.delivery_trips : data?.delivery_trips,
                        };
                        setLocalData(newData);
                    }}
                    fetchProductOrder={fetchSaleOrder}
                />
            </ProductOrderWrapper>
            <Dialog
                open={isManufacturingOrderDetailOpen}
                onClose={() => {
                    if (!isUpdatingManufacturingOrderCode) {
                        setIsManufacturingOrderDetailOpen(false);
                        setEditingManufacturingOrder(null);
                        setManufacturingOrderCodeInput("");
                    }
                }}
                fullWidth
                maxWidth="md"
                PaperProps={{
                    sx: {
                        borderRadius: "8px",
                        boxShadow: "0 22px 52px rgba(16, 47, 67, 0.22)",
                        overflow: "hidden",
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        px: "20px",
                        py: "14px",
                        borderBottom: "1px solid #E5EAF1",
                        backgroundColor: "#F8FAFC",
                        fontFamily: "Noto Sans",
                        fontSize: "18px",
                        fontWeight: 700,
                        color: "#333739",
                    }}
                >
                    Lệnh sản xuất
                </DialogTitle>
                <DialogContent dividers={false} sx={{ p: 0 }}>
                    <Stack sx={{ maxHeight: "66vh", overflow: "auto", backgroundColor: "#FFFFFF" }}>
                        {manufacturingOrders.map((manufacturingOrder: any, index: number) => {
                            const productName = manufacturingOrder?.product?.name || "—";
                            const productCode = manufacturingOrder?.product?.product_code;
                            const relatedOrders = manufacturingOrder?.related_sale_orders || [];
                            const isEditingCode = editingManufacturingOrder?.id === manufacturingOrder.id;
                            return (
                                <Box
                                    key={manufacturingOrder.id}
                                    sx={{
                                        p: "14px 20px 16px",
                                        borderBottom: index === manufacturingOrders.length - 1 ? "none" : "1px solid #E5EAF1",
                                    }}
                                >
                                    <Stack direction="row" alignItems="center" spacing={1.25}>
                                        {isEditingCode ? (
                                            <>
                                                <TextField
                                                    autoFocus
                                                    size="small"
                                                    value={manufacturingOrderCodeInput}
                                                    onChange={(event) => setManufacturingOrderCodeInput(event.target.value)}
                                                    placeholder="Nhập mã lệnh sản xuất"
                                                    sx={{
                                                        flex: 1,
                                                        minWidth: 0,
                                                        "& .MuiInputBase-root": {
                                                            height: "36px",
                                                            borderRadius: "5px",
                                                            fontFamily: "Noto Sans",
                                                            fontSize: "14px",
                                                            fontWeight: 700,
                                                            color: "#0058A0",
                                                        },
                                                    }}
                                                />
                                                <StyledModalButton
                                                    variant="outlined"
                                                    onClick={() => {
                                                        setEditingManufacturingOrder(null);
                                                        setManufacturingOrderCodeInput("");
                                                    }}
                                                    disabled={isUpdatingManufacturingOrderCode}
                                                >
                                                    Huỷ
                                                </StyledModalButton>
                                                <LoadingButton
                                                    variant="contained"
                                                    text="Lưu"
                                                    isLoading={isUpdatingManufacturingOrderCode}
                                                    disabled={isUpdatingManufacturingOrderCode || !manufacturingOrderCodeInput.trim()}
                                                    onClick={onConfirmManufacturingOrderCode}
                                                />
                                            </>
                                        ) : (
                                            <>
                                                <Typography
                                                    sx={{
                                                        flex: 1,
                                                        minWidth: 0,
                                                        fontFamily: "Noto Sans",
                                                        fontSize: "16px",
                                                        fontWeight: 700,
                                                        color: "#0058A0",
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        whiteSpace: "nowrap",
                                                    }}
                                                    title={formatManufacturingOrderDisplayName(manufacturingOrder)}
                                                >
                                                    {formatManufacturingOrderDisplayName(manufacturingOrder)}
                                                </Typography>
                                                <StyledModalButton
                                                    variant="outlined"
                                                    onClick={() => onEditManufacturingOrderCode(manufacturingOrder)}
                                                >
                                                    Sửa
                                                </StyledModalButton>
                                            </>
                                        )}
                                    </Stack>
                                    <Stack direction="row" spacing={1} sx={{ mt: "10px", flexWrap: "wrap", rowGap: "8px" }}>
                                        <Chip
                                            size="small"
                                            label={productCode ? `${productCode} - ${productName}` : productName}
                                            sx={{ borderRadius: "5px", fontFamily: "Noto Sans", fontWeight: 600 }}
                                        />
                                        <Chip
                                            size="small"
                                            label={`SL: ${formatNumber(manufacturingOrder?.manufacturing_order_quantity || 0)}`}
                                            sx={{ borderRadius: "5px", fontFamily: "Noto Sans", fontWeight: 600 }}
                                        />
                                    </Stack>
                                    <Box sx={{ mt: "12px" }}>
                                        <Typography sx={{ fontFamily: "Noto Sans", fontSize: "13px", color: "#526171", mb: "6px" }}>
                                            Đơn bán hàng
                                        </Typography>
                                        <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", rowGap: "8px" }}>
                                            {relatedOrders.map((order: any) => (
                                                <Button
                                                    key={order.id}
                                                    component={Link}
                                                    to={`/product-orders/${order.id}`}
                                                    size="small"
                                                    variant="text"
                                                    sx={{
                                                        minWidth: 0,
                                                        px: "8px",
                                                        py: "3px",
                                                        borderRadius: "5px",
                                                        backgroundColor: "#F1F5F9",
                                                        color: "#0058A0",
                                                        fontFamily: "Noto Sans",
                                                        fontSize: "13px",
                                                        fontWeight: 700,
                                                        textTransform: "none",
                                                        "&:hover": { backgroundColor: "#E6F2FB" },
                                                    }}
                                                >
                                                    {order?.order_name || "Đơn bán hàng"}
                                                </Button>
                                            ))}
                                        </Stack>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: "20px", py: "12px", borderTop: "1px solid #E5EAF1" }}>
                    <StyledModalButton
                        variant="outlined"
                        onClick={() => {
                            setIsManufacturingOrderDetailOpen(false);
                            setEditingManufacturingOrder(null);
                            setManufacturingOrderCodeInput("");
                        }}
                        disabled={isUpdatingManufacturingOrderCode}
                    >
                        Đóng
                    </StyledModalButton>
                </DialogActions>
            </Dialog>
            <Dialog
                open={isManufacturingCandidateOpen}
                onClose={() => {
                    if (!isCreatingManufacturingOrder) {
                        setIsManufacturingCandidateOpen(false);
                    }
                }}
                fullWidth
                maxWidth="lg"
                PaperProps={{
                    sx: {
                        borderRadius: "8px",
                        boxShadow: "0 22px 52px rgba(16, 47, 67, 0.22)",
                        overflow: "hidden",
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        px: "20px",
                        py: "14px",
                        borderBottom: "1px solid #E5EAF1",
                        backgroundColor: "#F8FAFC",
                        fontFamily: "Noto Sans",
                        fontSize: "18px",
                        fontWeight: 700,
                        color: "#333739",
                    }}
                >
                    Tạo lệnh sản xuất
                </DialogTitle>
                <DialogContent dividers sx={{ p: 0 }}>
                    <Stack sx={{ maxHeight: "70vh", overflow: "auto" }}>
                        {manufacturingCandidateGroups.map((group) => {
                            const productId = String(group?.product?.id || "");
                            const selectedIds = new Set(selectedManufacturingOrdersByProduct[productId] || []);
                            const productName = group?.product?.product_code
                                ? `${group.product.product_code} - ${group.product.name}`
                                : group?.product?.name || "Sản phẩm";
                            return (
                                <Box
                                    key={productId}
                                    sx={{
                                        p: "16px 20px 20px",
                                        borderBottom: "1px solid #E5EAF1",
                                    }}
                                >
                                    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: "12px" }}>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography
                                                sx={{
                                                    fontFamily: "Noto Sans",
                                                    fontSize: "16px",
                                                    fontWeight: 700,
                                                    color: "#1F2937",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                {productName}
                                            </Typography>
                                            <Stack direction="row" spacing={1} sx={{ mt: "6px", flexWrap: "wrap", rowGap: "6px" }}>
                                                <Chip size="small" label={group.customer?.name || "Khách hàng"} sx={{ borderRadius: "5px", fontFamily: "Noto Sans" }} />
                                                <Chip size="small" label={group.due_date} sx={{ borderRadius: "5px", fontFamily: "Noto Sans" }} />
                                                {group.existing_manufacturing_order && (
                                                    <Chip
                                                        size="small"
                                                        label={formatManufacturingOrderDisplayName(group.existing_manufacturing_order)}
                                                        sx={{
                                                            borderRadius: "5px",
                                                            fontFamily: "Noto Sans",
                                                            color: "#0058A0",
                                                            backgroundColor: "#E6F2FB",
                                                        }}
                                                    />
                                                )}
                                                <Chip
                                                    size="small"
                                                    label={`SL LSX: ${formatNumber(
                                                        group.manufacturing_order_quantity_preview ?? 0,
                                                    )}`}
                                                    sx={{
                                                        borderRadius: "5px",
                                                        fontFamily: "Noto Sans",
                                                        color: "#155E75",
                                                        backgroundColor: "#E0F2FE",
                                                    }}
                                                />
                                                <Chip
                                                    size="small"
                                                    label={`Đã chọn: ${selectedIds.size} đơn`}
                                                    sx={{
                                                        borderRadius: "5px",
                                                        fontFamily: "Noto Sans",
                                                        color: "#166534",
                                                        backgroundColor: "#DCFCE7",
                                                    }}
                                                />
                                            </Stack>
                                        </Box>
                                        <StyledModalButton
                                            variant="outlined"
                                            onClick={() => selectEligibleManufacturingCandidates(group)}
                                            disabled={isCreatingManufacturingOrder}
                                        >
                                            Chọn đơn đủ điều kiện
                                        </StyledModalButton>
                                        <StyledModalButton
                                            variant="text"
                                            onClick={() => clearOptionalManufacturingCandidates(group)}
                                            disabled={isCreatingManufacturingOrder}
                                        >
                                            Bỏ chọn thêm
                                        </StyledModalButton>
                                    </Stack>
                                    <Table size="small" sx={{ border: "1px solid #E5EAF1", borderRadius: "6px" }}>
                                        <TableHead>
                                            <TableRow sx={{ backgroundColor: "#F8FAFC" }}>
                                                <TableCell sx={{ width: 52 }} />
                                                <TableCell sx={{ fontFamily: "Noto Sans", fontWeight: 700 }}>Đơn bán hàng</TableCell>
                                                <TableCell align="right" sx={{ width: 150, fontFamily: "Noto Sans", fontWeight: 700 }}>SL thiếu của đơn</TableCell>
                                                <TableCell sx={{ width: 240, fontFamily: "Noto Sans", fontWeight: 700 }}>Trạng thái</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {(group.candidates || []).map((candidate) => {
                                                const checked = selectedIds.has(candidate.id);
                                                const disabled =
                                                    isCreatingManufacturingOrder ||
                                                    candidate.is_current_order ||
                                                    candidate.is_already_in_manufacturing_order ||
                                                    !candidate.has_required_factory_assignment;
                                                return (
                                                    <TableRow key={candidate.id} hover>
                                                        <TableCell>
                                                            <Checkbox
                                                                size="small"
                                                                checked={checked}
                                                                disabled={disabled}
                                                                onChange={() => toggleManufacturingCandidate(productId, candidate.id)}
                                                            />
                                                        </TableCell>
                                                        <TableCell sx={{ fontFamily: "Noto Sans", fontSize: "14px" }}>
                                                            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap", rowGap: "6px" }}>
                                                                <Box sx={{ fontWeight: 700 }}>{candidate.order_name}</Box>
                                                                {candidate.is_current_order && (
                                                                    <Chip size="small" label="Đơn hiện tại" sx={{ borderRadius: "5px", fontFamily: "Noto Sans" }} />
                                                                )}
                                                                {Number(candidate.missing_qty || 0) > 0 && (
                                                                    <Chip
                                                                        size="small"
                                                                        label="Đơn thiếu"
                                                                        sx={{ borderRadius: "5px", fontFamily: "Noto Sans", color: "#92400E", backgroundColor: "#FEF3C7" }}
                                                                    />
                                                                )}
                                                                {candidate.is_already_in_manufacturing_order && (
                                                                    <Chip
                                                                        size="small"
                                                                        label="Đã trong LSX"
                                                                        sx={{ borderRadius: "5px", fontFamily: "Noto Sans", color: "#0058A0", backgroundColor: "#E6F2FB" }}
                                                                    />
                                                                )}
                                                                {checked && !candidate.is_current_order && !candidate.is_already_in_manufacturing_order && Number(candidate.missing_qty || 0) === 0 && (
                                                                    <Chip
                                                                        size="small"
                                                                        label="Cùng nhóm LSX"
                                                                        sx={{ borderRadius: "5px", fontFamily: "Noto Sans", color: "#166534", backgroundColor: "#DCFCE7" }}
                                                                    />
                                                                )}
                                                            </Stack>
                                                        </TableCell>
                                                        <TableCell align="right" sx={{ fontFamily: "Noto Sans", fontWeight: 700 }}>
                                                            {formatNumber(candidate.missing_qty || 0)}
                                                        </TableCell>
                                                        <TableCell sx={{ fontFamily: "Noto Sans", fontSize: "13px", color: candidate.has_required_factory_assignment ? "#526171" : "#B42318" }}>
                                                            {candidate.has_required_factory_assignment ? "Đủ bàn giao xưởng" : candidate.assignment_message || "Thiếu bàn giao xưởng"}
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </Box>
                            );
                        })}
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: "20px", py: "12px", borderTop: "1px solid #E5EAF1" }}>
                    <StyledModalButton
                        variant="outlined"
                        onClick={() => setIsManufacturingCandidateOpen(false)}
                        disabled={isCreatingManufacturingOrder}
                    >
                        Huỷ
                    </StyledModalButton>
                    <LoadingButton
                        variant="contained"
                        text="Tạo lệnh sản xuất"
                        isLoading={isCreatingManufacturingOrder}
                        disabled={isCreatingManufacturingOrder || selectedManufacturingOrderTotal === 0}
                        onClick={onCreateManufacturingOrder}
                    />
                </DialogActions>
            </Dialog>
            <ConfirmModal
                open={isCompleteConfirmOpen}
                handleClose={() => setIsCompleteConfirmOpen(false)}
                isLoading={isUpdatingStatus}
                handleConfirm={async () => {
                    try {
                        const res: any = await postUpdateStatus({
                            status: SALE_ORDER_STATUS.COMPLETED,
                            notes: productOrder?.note || "",
                        });
                        await fetchSaleOrder();
                        const prev = String(res?.previous_status || "").toUpperCase();
                        const prevLabel =
                            valueToText(
                                ProductOrderFilterType,
                                "status",
                                prev,
                            ) || "Mở";
                        success("THÀNH CÔNG", `Đơn hàng ${prevLabel} hoàn thành`);
                    } catch (err) {
                        error("Đơn hàng hoàn thành thất bại", err as any);
                    } finally {
                        setIsCompleteConfirmOpen(false);
                    }
                }}
                confirmButtonText="Tiếp tục"
            >
                {completeWarningMessages.length === 2 ? (
                    <div className="tw-w-full tw-text-left">
                        <div
                            className="tw-bg-orange-50 tw-border-l-4 tw-border-orange-500 tw-rounded-md tw-py-2.5 tw-px-3 tw-text-orange-900 tw-text-[0.95rem] tw-leading-6">
                            {completeWarningMessages[0]}
                        </div>
                        <div className="tw-flex tw-items-center tw-my-2 tw-text-gray-500 tw-text-[0.8rem]">
                            <span className="tw-flex-1 tw-h-px tw-bg-gray-200" />
                            <span className="tw-mx-2 tw-font-semibold">hoặc</span>
                            <span className="tw-flex-1 tw-h-px tw-bg-gray-200" />
                        </div>
                        <div
                            className="tw-bg-cyan-50 tw-border-l-4 tw-border-cyan-500 tw-rounded-md tw-py-2.5 tw-px-3 tw-text-cyan-900 tw-text-[0.95rem] tw-leading-6">
                            {completeWarningMessages[1]}
                        </div>
                    </div>
                ) : (
                    <div className="tw-whitespace-pre-line tw-text-center">{completeWarningText}</div>
                )}
            </ConfirmModal>
        </>
    );
};

export default SaleOrderDetail;
